import { baseCamera2DData, baseCamera3DData, baseLayers, emptyClientInfo } from '@baseValues'
import { MorphDictionary } from '@components/Sidebar/Hole/HolePropertyInput'
import { getTexture } from '@proxies/getTexture'
import { getUnidadAgrupada } from '@proxies/getUnidadAgrupada'
import { openProyect } from '@proxies/openProyect'
import { setTextos, t } from '@proxies/translation'
import { setResource } from '@store'

function parsePrecioGuardado(value: unknown): number | undefined {
  if (typeof value === 'number' && !Number.isNaN(value) && value > 0) return value
  if (typeof value === 'string') {
    const cleaned = value.replace(/[^\d.,-]/g, '').replace(/\./g, '').replace(',', '.')
    const parsed = Number.parseFloat(cleaned)
    if (!Number.isNaN(parsed) && parsed > 0) return parsed
  }
  return undefined
}
import {
  AnyID,
  BackOpenProject,
  DesignerInfoPayload,
  Getter,
  Hole,
  Item,
  ItemGroup,
  ItemGroupID,
  LinkedItem,
  Modes,
  Room,
  savedView,
  Setter,
  SnappedSides,
  SnapSide,
  Store,
  View,
  Walls,
  Zone,
} from '@types'
import { calculateDeltasBetweenSelectedItems } from '@utils/calcDeltaItems'
import getStandardMaterial from '@utils/conversion/getStandardMaterial'
import toQuaternion from '@utils/conversion/toQuaternion'
import { toVector3 } from '@utils/conversion/toVector3'
import { radians } from '@utils/generalMaths/geometry'
import isHole from '@utils/sceneObjects/assertions/isHole'
import isItem from '@utils/sceneObjects/assertions/isItem'
import isLinkedItem from '@utils/sceneObjects/assertions/isLinkedItem'
import isWall from '@utils/sceneObjects/assertions/isWall'
import { computeItemGroupOBB } from '@utils/sceneObjects/computeItemGroupOBB'
import getId from '@utils/sceneObjects/getId'
import { getZonesFromGLB } from '@utils/sceneObjects/getZonesFromGLB'
import { rebuildSnappedSides } from '@utils/sceneObjects/rebuildSnappedSides'
import { getSnapCandidates } from '@utils/sceneObjects/snapGroups'
import { releaseSnappedSides } from '@utils/sceneObjects/snappedSides'
import { projectItemOnSlabFaces } from '@utils/sceneObjects/projectItemOnSlabFaces'
import { isAccesorioFregaderoCatalog } from '@utils/sceneObjects/isAccesorioFregaderoCatalog'
import {
  clampMorphMm,
  getFreeDimensionAltoGlbMorphMm,
  getFreeDimensionStoreAlto,
  isFreeDimensionAnchoApiMorphCatalog,
  isFreeDimensionDimensionMorph,
  itemUsesFreeDimensionAltoApiMorph,
  itemUsesFreeDimensionAltoGlbMorph,
  itemUsesFreeDimensionAnchoGlbMorph,
  itemUsesFreeDimensionFondoGlbMorph,
  logFreeDimensionAncho,
  logFreeDimensionFondo,
  morphHasValidRange,
  ensureFreeDimensionCatalogBases,
  isFreeDimensionLateralOrCostadoCatalog,
  usesFreeDimensionScaling,
  usesPureGroupFreeDimensionScale,
} from '@utils/sceneObjects/linked/freeDimensionLinkedCatalogs'
import { kbrAltoAllowsBeyondMorphMax } from '@utils/sceneObjects/linked/integratedZocaloAlto'

/** Proyectos antiguos: sellar free-dim y alinear glbPath al catálogo actual. */
function stampLegacyFreeDimensionBases(item: any) {
  if (!item || typeof item !== 'object') return item
  if (item.type === 'walls' || item.type === 'holes' || item.type === 'areas') return item
  if (usesFreeDimensionScaling(item) || usesPureGroupFreeDimensionScale(item)) {
    ensureFreeDimensionCatalogBases(item)
  }
  return item
}

/** Debounce: al cambiar Alto/Fondo/Ancho de un mueble, reescalar/reposicionar laterales. */
const lateralsSyncTimeoutByFurniture = new Map<string, ReturnType<typeof setTimeout>>()
import {
  buildDimFlowSnapshot,
  logDimFlow,
} from '@utils/debug/dimensionFlowDiagnostic'
import { reconcileProjectTexturesOnLoad } from '@utils/sceneObjects/linked/reconcileTexturesOnProjectLoad'
import { remapStaleLinkedZonesOnProjectLoad } from '@utils/sceneObjects/linked/remapStaleLinkedZonesOnProjectLoad'
import {
  applyProjectUnidadesDimensionsToItems,
  hydrateLegacyUnitsToDrawItemParity,
} from '@utils/sceneObjects/linked/ensureIndependentZocaloOnProjectLoad'
import { purgeSceneGlbForCatalog } from '@utils/sceneObjects/purgeSceneGlbForCatalog'
import { removeLinkedHierarchyFromStore } from '@utils/sceneObjects/removeLinkedHierarchyFromStore'
import {
  chunkCatalogJobs,
  collectProjectCatalogJobs,
  filterBoundingBoxesForItems,
  filterItemsForOpenLayer,
  loadProjectCatalogJob,
  loadProjectCatalogJobs,
} from '@utils/sceneObjects/loadProjectCatalogGlbs'
import {
  beginSceneCostSession,
  endSceneCostSession,
  markSceneCostPhase,
  readSceneCostGpu,
  refreshSceneCostGpu,
} from '@utils/debug/sceneCostProfiler'
import {
  getProjectFurnitureBatchSize,
  isLightweightViewer,
  yieldToMainThread,
} from '../viewport/readCapabilities'
import { releasePreviousPlannerGlbs } from '@utils/webgl/releasePlannerAssets'
import { clearGlbUrlCache } from '@utils/sceneObjects/glbLoaderCache'
import { getWallCOBB } from '@utils/sceneObjects/walls/getWallCOBB'
import { getInitialToolbarMenuColumns } from '@utils/toolbarMenuColumns'
import { normalizeInspectorDismissed } from '@utils/kitchenInspector/dismissed'
import SuperJSON from 'superjson'
import { Euler, Matrix3, Matrix4, Quaternion, Vector3 } from 'three'
import { OBB } from 'three/examples/jsm/math/OBB'

export type ProjectActions = ReturnType<typeof createProjectActions>

export const createProjectActions = (set: Setter, get: Getter) => ({
  setMode: (mode: Modes) =>
    set((store) => {
      store.mode = mode
      // Check global: desactivar isOutlineMode solo al cambiar a modo 3D (excepto alzados)
      if (mode === 'MODE_3D_VIEW' || mode === 'MODE_DRAGGING_ITEM_3D' || mode === 'MODE_FORCE_3D') {
        store.isOutlineMode = false
      }
    }),
  setSelectedRoom: (room: Room) =>
    set((store) => {
      if (!room) return /* console.warn('Room not provided') */
      store.selectedRoom = room
    }),

  setPreference: <T extends keyof Store['prefs']>(name: T, value: Store['prefs'][T]) => {
    set((store) => {
      const prefs = store.prefs
      prefs[name] = value
    })
  },
  editPrice: (price: number) => {
    set((store) => {
      store.price += price
    })
  },
  calculatePrice: () => {
    set((store) => {
      const items = store.layers[store.currentLayer].items
      const newPrice = Array.from(items).reduce((acc, el) => {
        const item = store.items.get(el) as { price: number }

        if (!item) return acc
        const itemPrice = item?.price
        if (!itemPrice) return acc
        return (acc += itemPrice)
      }, 0)
      store.price = newPrice
    })
  },
  setLanguageOptions: (options: Store['textos']['idioma'][]) => {
    //TODO: check what the back gives us
    set((store) => {
      store.languageOptions = options
    })
  },
  setCameraMove: (newState?: boolean) => {
    set((store) => {
      store.shouldCameraMove = newState !== undefined ? newState : !store.shouldCameraMove
    })
  },
  setCanDraw: (bool: boolean) => {
    set((store) => {
      store.canDraw = bool
    })
  },
  t: (text: keyof Store['textos']['textos']) => t(set, get, text),
  setTextos: (textos: Store['textos']) => {
    setTextos(get, set, textos)
  },
  setIdiomaCultura: (id: string) => {
    set((store) => {
      store.idiomaCultura = id
    })
  },
  setClientInfo: (clientInfo: Store['clientInfo']) => {
    set((store) => {
      store.clientInfo = {
        ...clientInfo,
      }
    })
  },
  setPendingDesignerInfo: (payload: DesignerInfoPayload | null) => {
    set((store) => {
      store.pendingDesignerInfo = payload
    })
  },
  setPrefs: (prefs: Store['prefs'], prefsInfo: Store['prefsInfo']) => {
    set((store) => {
      store.prefsInfo = prefsInfo
      const normalized = { ...prefs } as Store['prefs']
      normalized.COLUMNAMENU = getInitialToolbarMenuColumns(
        normalized as unknown as Record<string, unknown>,
      )
      store.prefs = normalized
      setResource('prefs', true)
    })
  },
  setTextures: async <S extends string, N extends number>(
    catalogo: S,
    unidadsuelo: N,
    unidadpared0: N,
    unidadpared1: N,
    unidadtecho: N,
    unidadtabique0: N,
    unidadtabique1: N,
    unidadtabique2: N,
    unidadtabique3: N,
    unidadtabique4: N,
    unidadtabique5: N,
    token?: string,
  ) => {
    // prefs?.CATALOGOUNIVERSAL &&
    //       prefs?.UNIDADSUELO &&
    //       prefs?.UNIDADPARED0 &&
    //       prefs?.UNIDADPARED1 &&
    //       prefs?.UNIDADTECHO
    const catalog = get().prefs['CATALOGOUNIVERSAL'] ?? catalogo
    const isLogged = window.localStorage.getItem('isUserLogged')
    const texture1 = await getTexture(catalog, unidadsuelo, token)
    const texture2 = await getTexture(catalog, unidadpared0, token)
    const texture3 = await getTexture(catalog, unidadpared1, token)
    const texture4 = await getTexture(catalog, unidadtecho, token)
    const texture5 = await getTexture(catalog, unidadtabique0, token)
    const texture6 = await getTexture(catalog, unidadtabique1, token)
    const texture7 = await getTexture(catalog, unidadtabique2, token)
    const texture8 = await getTexture(catalog, unidadtabique3, token)
    const texture9 = await getTexture(catalog, unidadtabique4, token)
    const texture10 = await getTexture(catalog, unidadtabique5, token)

    if (
      !texture1 ||
      !texture2 ||
      !texture3 ||
      !texture4 ||
      !texture5 ||
      !texture6 ||
      !texture7 ||
      !texture8 ||
      !texture9 ||
      !texture10
    )
      return

    texture2.name = 'MSG_94'
    texture2.id = 'textureA'
    texture3.name = 'MSG_95'
    texture3.id = 'textureB'
    texture1.id = 'areaSuelo'
    texture1.name = 'MSG_182'
    texture4.id = 'areaTecho'
    texture4.name = 'MSG_221'
    texture5.id = 'tabiqueCara0'
    texture5.name = 'MSG_221'
    texture6.id = 'tabiqueCara1'
    texture6.name = 'MSG_221'
    texture7.id = 'tabiqueCara2'
    texture7.name = 'MSG_221'
    texture8.id = 'tabiqueCara3'
    texture8.name = 'MSG_221'
    texture9.id = 'tabiqueCara4'
    texture9.name = 'MSG_221'
    texture10.id = 'tabiqueCara5'
    texture10.name = 'MSG_221'

    const items = new Map()
    const areas = new Map()

    set((store) => {
      store.texture1 = texture1
      store.texture2 = texture2
      store.texture3 = texture3
      store.texture4 = texture4
      store.texture5 = texture5
      store.texture6 = texture6
      store.texture7 = texture7
      store.texture8 = texture8
      store.texture9 = texture9
      store.texture10 = texture10
      if (!isLogged) {
        store.items = items
        store.areas = areas
      }
      setResource('texturas', true)
    })
  },
  resetTextures: async <S extends string, N extends number>(
    catalogo: S,
    unidadsuelo: N,
    unidadpared0: N,
    unidadpared1: N,
    unidadtecho: N,
    unidadtabique0: N,
    unidadtabique1: N,
    unidadtabique2: N,
    unidadtabique3: N,
    unidadtabique4: N,
    unidadtabique5: N,
  ) => {
    const texture1 = await getTexture(catalogo, unidadsuelo)
    const texture2 = await getTexture(catalogo, unidadpared0)
    const texture3 = await getTexture(catalogo, unidadpared1)
    const texture4 = await getTexture(catalogo, unidadtecho)
    const texture5 = await getTexture(catalogo, unidadtabique0)
    const texture6 = await getTexture(catalogo, unidadtabique1)
    const texture7 = await getTexture(catalogo, unidadtabique2)
    const texture8 = await getTexture(catalogo, unidadtabique3)
    const texture9 = await getTexture(catalogo, unidadtabique4)
    const texture10 = await getTexture(catalogo, unidadtabique5)
    if (!texture1 || !texture2 || !texture3 || !texture4) return

    texture2.name = 'MSG_94'
    texture2.id = 'textureA'
    texture3.name = 'MSG_95'
    texture3.id = 'textureB'
    texture1.id = 'areaSuelo'
    texture1.name = 'MSG_182'
    texture4.id = 'areaTecho'
    texture4.name = 'MSG_221'
    texture5.id = 'tabiqueCara0'
    texture5.name = 'MSG_221'
    texture6.id = 'tabiqueCara1'
    texture6.name = 'MSG_221'
    texture7.id = 'tabiqueCara2'
    texture7.name = 'MSG_221'
    texture8.id = 'tabiqueCara3'
    texture8.name = 'MSG_221'
    texture9.id = 'tabiqueCara4'
    texture9.name = 'MSG_221'
    texture10.id = 'tabiqueCara5'
    texture10.name = 'MSG_221'

    set((store) => {
      store.texture1 = getStandardMaterial(texture1)
      store.texture2 = getStandardMaterial(texture2)
      store.texture3 = getStandardMaterial(texture3)
      store.texture4 = getStandardMaterial(texture4)
      store.texture5 = getStandardMaterial(texture5)
      store.texture6 = getStandardMaterial(texture6)
      store.texture7 = getStandardMaterial(texture7)
      store.texture8 = getStandardMaterial(texture8)
      store.texture9 = getStandardMaterial(texture9)
      store.texture10 = getStandardMaterial(texture10)
      setResource('texturas', true)
    })
  },

  setCamera3D: (data: Store['camera3DData']) => {
    set((store) => {
      store.camera3DData = data
    })
  },
  setCamera2DPosition: (position: Store['camera2DPosition']) => {
    set((store) => {
      store.camera2DPosition = position
    })
  },
  setLastView: (lastView: Store['lastView']) => {
    set((store) => {
      store.lastView = lastView
    })
  },
  addSavedView: (newView: savedView) => {
    set((store) => {
      store.savedViews = [...(store.savedViews || []), newView]
    })
  },
  deleteSavedView: (index: number) => {
    set((store) => ({
      savedViews: store.savedViews.filter((_, i) => i !== index),
    }))
  },
  setSavedViews: (views: View[]) => {
    set(() => ({
      savedViews: views,
    }))
  },
  setStoreFromBack: async (
    id: number,
    tmp = false,
    comesFromStances = false,
    projectInfo?: DesignerInfoPayload | null,
    precioGuardado?: number,
  ) => {
    const fallbackDesignerInfo = get().pendingDesignerInfo ?? null
    const designerInfo = projectInfo ?? fallbackDesignerInfo ?? null

    const backResponse = await openProyect(id, tmp)

    // Resetear estado del modal de unidades para nuevos proyectos
    set((store) => {
      store.modalUnidadesMostrado = false
      store.unidadesFaltantes = []
      store.mostrarModalUnidadesFaltantes = false
    })

    // Validar unidades del JSON contra el catálogo disponible
    const validateUnidades = (jsonString: string, catalogoUnidades: any[], t: any) => {
      try {
        // El jsonString ya viene como string del backResponse.json
        const json = JSON.parse(jsonString)
        const unidadesEnJson = new Set()

        // Función recursiva para buscar todos los idUnidades en cualquier parte del JSON
        const buscarIdUnidadesEnObjeto = (obj: any, path: string = '') => {
          if (typeof obj !== 'object' || obj === null) return

          // Si el objeto tiene idUnidades Y es de tipo linkedItem, agregarlo
          if (obj.idUnidades !== undefined && obj.type === 'linkedItem') {
            unidadesEnJson.add(obj.idUnidades)
          }

          // Recorrer todas las propiedades del objeto
          for (const key in obj) {
            if (obj.hasOwnProperty(key)) {
              const value = obj[key]
              const newPath = path ? `${path}.${key}` : key

              if (typeof value === 'object' && value !== null) {
                if (Array.isArray(value)) {
                  // Si es un array, recorrer cada elemento
                  value.forEach((item, index) => {
                    buscarIdUnidadesEnObjeto(item, `${newPath}[${index}]`)
                  })
                } else {
                  // Si es un objeto, recorrerlo recursivamente
                  buscarIdUnidadesEnObjeto(value, newPath)
                }
              }
            }
          }
        }

        buscarIdUnidadesEnObjeto(json, 'json')

        // Convertir catálogo a Set para fácil búsqueda
        const catalogoUnidadesSet = new Set(catalogoUnidades.map((u) => u.idUnidades))

        // Encontrar unidades faltantes
        const unidadesFaltantes = Array.from(unidadesEnJson).filter(
          (id) => !catalogoUnidadesSet.has(id),
        )

        if (unidadesFaltantes.length > 0) {
          // Obtener detalles de las unidades faltantes
          const detallesFaltantes = unidadesFaltantes.map((id) => {
            // Buscar en el JSON para obtener referencia si existe
            let referencia = 'Desconocida'

            const buscarReferencia = (obj: any) => {
              if (typeof obj !== 'object' || obj === null) return

              if (obj.idUnidades === id && obj.referencia) {
                referencia = obj.referencia
                return true
              }

              for (const key in obj) {
                if (obj.hasOwnProperty(key)) {
                  const value = obj[key]
                  if (typeof value === 'object' && value !== null) {
                    if (Array.isArray(value)) {
                      for (const item of value) {
                        if (buscarReferencia(item)) return true
                      }
                    } else {
                      if (buscarReferencia(value)) return true
                    }
                  }
                }
              }
              return false
            }

            buscarReferencia(json)

            if (referencia === 'Desconocida') {
              // console.log(`   ❌ No se encontró referencia para ID: ${id}`)
            }

            return { idUnidades: id, referencia }
          })

          // Guardar las unidades faltantes en el store para que el componente React las muestre después del spinner
          set((store) => {
            store.unidadesFaltantes = detallesFaltantes
            // No mostrar el modal aquí, se mostrará después del setTimeout
          })

          return { tieneFaltantes: true, unidades: detallesFaltantes }
        }

        return { tieneFaltantes: false, unidades: [] }
      } catch (error) {
        console.error('Error validando unidades:', error)
        return { tieneFaltantes: false, unidades: [] }
      }
    }

    beginSceneCostSession()
    try {
    markSceneCostPhase('json+items')
    const info = SuperJSON.parse(backResponse.json) as BackOpenProject
    const layeredOpen = isLightweightViewer()
    const structureJobs = collectProjectCatalogJobs(info.items.values(), true)
    const furnitureJobs = collectProjectCatalogJobs(info.items.values(), false)
    const previousGlbs = Array.from(get().sceneGLB.values()) as Array<{ glb?: unknown }>

    if (layeredOpen) {
      clearGlbUrlCache()
    }

    set((store) => {
      store.sceneGLB = new Map()
      store.sceneBoundingBoxes = new Map()
    })

    const items = new Map()
    info.items?.forEach((item) => {
      // TODO: partitions
      if (item.type === 'walls') {
        if (item.perpendicularPoint) {
          const { x, y, z } = item.perpendicularPoint
          const pp = toVector3([x, y, z])
          const { x: _x, y: _y, z: _z } = item.symmetricalPerpendicularPoint
          const spp = toVector3([_x, _y, _z])
          items.set(item.id, {
            ...item,
            perpendicularPoint: pp,
            symmetricalPerpendicularPoint: spp,
          })
          return
        }
      }
      if (item?.zones) {
        const itemCopy = stampLegacyFreeDimensionBases({ ...item })
        for (const zoneName in itemCopy.zones) {
          const zone = { ...itemCopy.zones[zoneName] }
          // Check if the zone has a valid position and rotation
          if (zone.position && zone.rotation) {
            const fixedPosition = toVector3([zone.position.x, zone.position.y, zone.position.z])
            const fixedRotation = new Euler(
              zone.rotation._x,
              zone.rotation._y,
              zone.rotation._z,
              zone.rotation._order,
            )
            const fixedScale = toVector3([zone.scale.x, zone.scale.y, zone.scale.z])
            zone.scale = fixedScale
            zone.position = fixedPosition
            zone.rotation = fixedRotation
          }
          // Handle other textures of the zone if needed
          // For example, you can access zone.scale or other textures here
        }
        items.set(item.id, itemCopy)
      } else {
        items.set(item.id, stampLegacyFreeDimensionBases(item))
      }
      if (item?.matrix) {
        const itemCopy = stampLegacyFreeDimensionBases({ ...item })
        const fixedMatrix = new Matrix4().fromArray(item.matrix.elements)
        itemCopy.matrix = fixedMatrix
        items.set(item.id, itemCopy)
      } else {
        items.set(item.id, stampLegacyFreeDimensionBases(item))
      }
    })
    const sceneBoundingBoxes = new Map()
    for (const [key, value] of info.sceneBoundingBoxes.entries()) {
      const item = items.get(key)
      // if (!item) continue
      const type = value.type
      const { x, y, z } = value.boundingBox.center
      const { x: sizeX, y: sizeY, z: sizeZ } = value.boundingBox.halfSize
      if (key.startsWith('COBB')) {
        const finalKey = key.replace('COBB_', '')
        const actualItem = items.get(finalKey)
        if (!actualItem) continue
        const rotation = actualItem.rotation
        const boundingBox = new OBB(toVector3([x, y, z]), toVector3([sizeX, sizeY, sizeZ]))
        const collisionOBB = new OBB(
          toVector3([x, 1, z]),
          toVector3([0, actualItem.height, actualItem.thickness / 2]),
        )
        const mat4 = new Matrix4().makeRotationFromEuler(
          new Euler(0, -rotation + 2 * Math.PI, 0, 'XYZ'),
        )
        const rot = new Matrix3().setFromMatrix4(mat4)
        boundingBox.rotation = rot
        sceneBoundingBoxes.set(key, { type, boundingBox: collisionOBB })
        continue
      }
      if (!item) continue
      const boundingBox = new OBB(toVector3([x, y, z]), toVector3([sizeX, sizeY, sizeZ]))
      sceneBoundingBoxes.set(key, { type, boundingBox })
    }

    const precioInicial = parsePrecioGuardado(precioGuardado) ?? info.price ?? 0

    const commitOpenedProject = (
      nextItems: Map<any, any>,
      nextBoxes: Map<any, any>,
      extras?: { textureFixed?: number; zoneRemapped?: number },
    ) => {
      set((store) => {
        store.items = nextItems
        store.areas = info.areas
        store.layers = info.layers
        store.price = precioInicial
        store.storeHistory = []
        store.layers[store.currentLayer].selected.clear()
        store.canDraw = true
        store.currentArea = ''
        store.currentFirstWall = ''
        store.currentLayer = info.currLayer
        store.currentObjectID = ''
        store.mode = 'MODE_IDLE'
        store.sceneBoundingBoxes = nextBoxes
        if (!comesFromStances) {
          const disenadorActual =
            designerInfo?.disenadorActual ?? backResponse.disenyo.disenadorActual ?? null
          const disenadorOriginal =
            designerInfo?.disenadorOriginal ?? backResponse.disenyo.disenadorOriginal ?? null
          const numProyectoTpc =
            designerInfo?.numProyectoTpc ?? backResponse.disenyo.numProyectoTpc ?? null

          store.clientInfo = {
            nombreProyecto: backResponse.disenyo.nombreProyecto,
            descripcion: backResponse.disenyo.descripcion,
            nombre: backResponse.disenyo.nombre,
            apellidos: backResponse.disenyo.apellidos,
            direccion: backResponse.disenyo.direccion,
            codPostal: backResponse.disenyo.codPostal,
            poblacion: backResponse.disenyo.poblacion,
            email: backResponse.disenyo.email,
            pais: backResponse.disenyo.pais,
            movil: backResponse.disenyo.movil?.toString?.() ?? '',
            numProyectoTpc: numProyectoTpc ?? undefined,
            idDisenos: backResponse.disenyo.idDisenos,
            idVersiones: backResponse.disenyo.idVersiones,
            idImpuestos: backResponse.disenyo.idImpuestos ?? 0,
            disenadorActual,
            disenadorOriginal,
          }
        } else {
          store.clientInfo = {
            ...emptyClientInfo,
            nombreProyecto: backResponse.disenyo.nombreProyecto,
            numProyectoTpc: backResponse.disenyo.numProyectoTpc ?? undefined,
            disenadorActual: backResponse.disenyo.disenadorActual ?? null,
            disenadorOriginal: backResponse.disenyo.disenadorOriginal ?? null,
            movil: backResponse.disenyo.movil?.toString?.() ?? '',
          }
        }
        store.collisionableItems = info.collisionableItems ?? new Set()
        store.isProjectOpened = true
        store.savedViews = info.savedViews || []
        store.inspectorDismissed = normalizeInspectorDismissed(info.inspectorDismissed)
        store.esMio = backResponse.esMio ?? true
        store.pendingDesignerInfo = null
        if ((extras?.textureFixed ?? 0) > 0 || (extras?.zoneRemapped ?? 0) > 0) {
          store.contadorModificaciones = (store.contadorModificaciones || 0) + 1
        }
      })
    }

    markSceneCostPhase('glb-structure')
    await loadProjectCatalogJobs(structureJobs, set, undefined, items)

    if (layeredOpen) {
      const structureItems = filterItemsForOpenLayer(items, null)
      commitOpenedProject(structureItems, filterBoundingBoxesForItems(sceneBoundingBoxes, structureItems))
      await yieldToMainThread()
      releasePreviousPlannerGlbs(previousGlbs)

      markSceneCostPhase('glb-furniture')
      const loadedFurniture = new Set<string>()
      for (const batch of chunkCatalogJobs(furnitureJobs, getProjectFurnitureBatchSize())) {
        await Promise.all(batch.map((job) => loadProjectCatalogJob(job, set, undefined, items)))
        for (const job of batch) loadedFurniture.add(job.key)
        const layerItems = filterItemsForOpenLayer(items, loadedFurniture)
        commitOpenedProject(layerItems, filterBoundingBoxesForItems(sceneBoundingBoxes, layerItems))
        await yieldToMainThread()
      }
    } else {
      markSceneCostPhase('glb-furniture')
      await loadProjectCatalogJobs(furnitureJobs, set, undefined, items)
    }

    markSceneCostPhase('remap+textures')
    // Solo excepciones (p.ej. 2111+KAB5060): resto siempre desde el JSON.
    applyProjectUnidadesDimensionsToItems(items, backResponse.unidades as any, undefined, {
      idVersiones: backResponse.disenyo?.idVersiones,
    })

    // Proyectos antiguos: el GLB del catálogo puede haber renombrado meshes
    // (p.ej. RA905 Cube002_* → Cube001_*). Remapear zones/textures antes del reconcile.
    const staleZoneRemap = remapStaleLinkedZonesOnProjectLoad(items, get().sceneGLB)
    const textureReconciliation = await reconcileProjectTexturesOnLoad(items)

    // El agrupado de muebles (encimeras, laterales, frontales) sale solo de snappedSides, y
    // los proyectos guardados antes de que existiera el registro llegan sin él: se
    // reconstruye una vez aquí, con la escena ya montada. Ver rebuildSnappedSides.
    rebuildSnappedSides(items, getSnapCandidates(items.values()))

    commitOpenedProject(items, sceneBoundingBoxes, {
      textureFixed: textureReconciliation.fixedCount,
      zoneRemapped: staleZoneRemap.remappedCount,
    })

    markSceneCostPhase('hydrate')
    // Paridad con drawItem: bases/morphs/zócalo/encimera/laterales.
    // Medidas = JSON; excepciones puntuales desde unidades.
    const upgraded = await hydrateLegacyUnitsToDrawItemParity({
      get,
      set,
      projectUnidades: backResponse.unidades as any,
      idVersiones: backResponse.disenyo?.idVersiones,
    })
    const postHydrateZoneRemap = remapStaleLinkedZonesOnProjectLoad(get().items, get().sceneGLB)
    if (upgraded || postHydrateZoneRemap.remappedCount > 0) {
      set((store) => {
        store.contadorModificaciones = (store.contadorModificaciones || 0) + 1
      })
    }

    // Validar unidades después de cargar todo (después del spinner)
    if (backResponse && backResponse.json && backResponse.unidades) {
      const t = get().t
      validateUnidades(backResponse.json, backResponse.unidades, t)
    }

    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('project-loaded', {
          detail: { idVersiones: backResponse.disenyo.idVersiones },
        }),
      )
    }
    } finally {
      try {
        endSceneCostSession(readSceneCostGpu())
      } catch (error) {
        console.error('[SceneCost] finally', error)
      }
      window.setTimeout(() => {
        refreshSceneCostGpu()
      }, 1600)
    }
  },
  addProjectItemsToCurrentScene: async (idVersiones: number) => {
    try {
      const start = performance.now()
      // Primero intentamos con tmp = false (carga completa, como setStoreFromBack)
      // Si el back no devuelve json en este modo (caso layouts/favoritos), hacemos
      // un fallback automático a tmp = true para mantener compatibilidad.
      let backResponse = await openProyect(idVersiones, false)
      const afterOpen = performance.now()

      // Fallback: si no hay json en modo completo, reintentar en modo tmp (true)
      if (!backResponse || !backResponse.json) {
        backResponse = await openProyect(idVersiones, true)
      }

      // Validar que backResponse existe y tiene la propiedad json
      if (!backResponse || !backResponse.json) {
        console.error(
          '❌ No se pudo obtener la respuesta del proyecto:',
          idVersiones,
          'Respuesta:',
          backResponse,
        )
        return false
      }

      const info = SuperJSON.parse(backResponse.json) as BackOpenProject

      // Validar que info tiene items
      if (!info || !info.items || info.items.size === 0) {
        console.error('❌ El proyecto no tiene items o está vacío:', idVersiones)
        return false
      }

      const isAlreadyLoaded = (catalogKey: string | number) => get().sceneGLB.has(catalogKey)
      await loadProjectCatalogJobs(
        [
          ...collectProjectCatalogJobs(info.items.values(), true),
          ...collectProjectCatalogJobs(info.items.values(), false),
        ],
        set,
        isAlreadyLoaded,
      )

      // Procesar items del proyecto y agregarlos al proyecto actual
      const newItems = new Map()
      const idMapping = new Map<string, string>() // Mapeo de IDs antiguos a nuevos
      const currentIncrement = get().increment
      let newIncrement = currentIncrement

      // Primero, crear mapeo de IDs y procesar todos los items
      const mapStart = performance.now()
      info.items?.forEach((item) => {
        // Generar nuevos IDs para evitar conflictos
        let newId: string
        if (item.type === 'walls') {
          newId = getId('walls')
        } else if (item.type === 'holes') {
          newId = getId('holes')
        } else if (item.type === 'linkedItem') {
          newId = getId('linkedItem')
        } else if (item.type === 'linkedSon') {
          newId = getId('linkedSon')
        } else if (item.type === 'items') {
          newId = getId('items')
        } else {
          newId = getId('items')
        }

        idMapping.set(item.id, newId)
      })

      // Ahora procesar items con los nuevos IDs
      const processItemsStart = performance.now()
      info.items?.forEach((item) => {
        const newId = idMapping.get(item.id)!
        if (!newId) return

        // Procesar item manteniendo su estructura
        let processedItem: any = { ...item, id: newId }

        // Procesar walls
        if (item.type === 'walls') {
          if (item.perpendicularPoint) {
            const { x, y, z } = item.perpendicularPoint
            const pp = toVector3([x, y, z])
            const { x: _x, y: _y, z: _z } = item.symmetricalPerpendicularPoint
            const spp = toVector3([_x, _y, _z])
            processedItem = {
              ...processedItem,
              perpendicularPoint: pp,
              symmetricalPerpendicularPoint: spp,
            }
          }
        }

        // Procesar zones
        if (item?.zones) {
          const itemCopy = { ...processedItem }
          for (const zoneName in itemCopy.zones) {
            const zone = { ...itemCopy.zones[zoneName] }
            if (zone.position && zone.rotation) {
              const fixedPosition = toVector3([zone.position.x, zone.position.y, zone.position.z])
              const fixedRotation = new Euler(
                zone.rotation._x,
                zone.rotation._y,
                zone.rotation._z,
                zone.rotation._order,
              )
              const fixedScale = toVector3([zone.scale.x, zone.scale.y, zone.scale.z])
              zone.scale = fixedScale
              zone.position = fixedPosition
              zone.rotation = fixedRotation
            }
          }
          processedItem = itemCopy
        }

        // Procesar matrix
        if (item?.matrix) {
          const fixedMatrix = new Matrix4().fromArray(item.matrix.elements)
          processedItem.matrix = fixedMatrix
        }

        // Actualizar childs para linkedItems si existen
        if (item.type === 'linkedItem' && item.childs) {
          const newChilds = new Set<string>()
          item.childs.forEach((childId: string) => {
            const newChildId = idMapping.get(childId)
            if (newChildId) {
              newChilds.add(newChildId)
            }
          })
          processedItem.childs = newChilds
        }

        // Las caras registradas guardan ids del proyecto de origen, y aquí todos se
        // regeneran: sin remapearlas el agrupado por snappedSides no vería ningún vecino.
        // Lo que no esté en el mapeo se cae (vecino que no viene en la importación).
        const savedSides = (item as { snappedSides?: SnappedSides }).snappedSides
        if (savedSides) {
          const remappedSides: SnappedSides = {}
          for (const side of Object.keys(savedSides) as SnapSide[]) {
            const occupantId = savedSides[side]
            const newOccupantId = occupantId ? idMapping.get(occupantId) : undefined
            if (newOccupantId) remappedSides[side] = newOccupantId as AnyID
          }
          processedItem.snappedSides = remappedSides
        }

        // Actualizar firstParentID para linkedSon
        if (item.type === 'linkedSon' && item.firstParentID) {
          const newParentId = idMapping.get(item.firstParentID)
          if (newParentId) {
            processedItem.firstParentID = newParentId
          }
        }

        newItems.set(newId, stampLegacyFreeDimensionBases(processedItem))
        newIncrement++
      })

      // Precalcular zonas y texturas para los nuevos items cuando sea posible
      // Esto ayuda a que, al cargar proyectos/favoritos, las texturas se apliquen sin esperar a interacciones
      const unidadesBack = (backResponse as any)?.unidades ?? []
      const retryTextureIds: string[] = []
      const texturesStart = performance.now()
      newItems.forEach((item) => {
        if (!item) return
        if (item.type === 'walls' || item.type === 'holes') return

        const key = (item as any).catalogID || (item as any).idUnidades
        if (!key) return

        const sceneEntry = get().sceneGLB.get(key)
        const glb = sceneEntry?.glb as any
        const backGLBInfo = sceneEntry?.backGLBInfo as any

        if (!glb) return

        // No machacar texturas si ya vienen en el proyecto
        const alreadyHasTextures =
          Array.isArray((item as any).textures) && (item as any).textures.length > 0
        if (alreadyHasTextures) return

        // Intentar usar acabados del backGLBInfo; si no, caer a los de backResponse.unidades
        const unidadFromBack = unidadesBack.find(
          (u: any) => u.idUnidades === (item as any).idUnidades,
        )

        const acabadosSource =
          (backGLBInfo && Array.isArray(backGLBInfo.acabados) && backGLBInfo.acabados.length > 0
            ? backGLBInfo.acabados
            : undefined) ||
          (unidadFromBack &&
          Array.isArray(unidadFromBack.acabados) &&
          unidadFromBack.acabados.length > 0
            ? unidadFromBack.acabados
            : undefined)

        if (!acabadosSource) return

        const extraInfo =
          unidadFromBack && Array.isArray(unidadFromBack.acabados)
            ? {
                zonas: unidadFromBack.acabados
                  .filter((a: any) => a && a.idAcabadoUnidad && a.zona)
                  .map((a: any) => ({
                    idAcabadoUnidad: a.idAcabadoUnidad as number,
                    zona: String(a.zona),
                  })),
              }
            : undefined

        const zonesAndTextures =
          getZonesFromGLB(glb.scene?.children, acabadosSource, extraInfo) || {}

        const { zones = {}, textures = [] } = zonesAndTextures as {
          zones: any
          textures: any[]
        }

        ;(item as any).zones = zones
        ;(item as any).textures = textures

        // Si tras el primer intento seguimos sin texturas, marcar para un reintento posterior
        if (!textures || textures.length === 0) {
          const idForRetry = (item as any).id
          if (idForRetry && typeof idForRetry === 'string') {
            retryTextureIds.push(idForRetry)
          }
        }
      })

      const staleZoneRemap = remapStaleLinkedZonesOnProjectLoad(newItems, get().sceneGLB)
      const textureReconciliation = await reconcileProjectTexturesOnLoad(newItems)

      // Solo excepciones (2111+KAB5060); resto desde JSON.
      applyProjectUnidadesDimensionsToItems(newItems, (backResponse as any)?.unidades, undefined, {
        idVersiones: (backResponse as any)?.disenyo?.idVersiones,
      })

      // Agregar items a la escena actual
      const addItemsStart = performance.now()
      set((store) => {
        // Agregar items al mapa existente
        newItems.forEach((item, id) => {
          store.items.set(id, item)
          // Agregar a la capa actual
          store.layers[store.currentLayer].items.add(id)
        })

        // Actualizar increment
        store.increment = newIncrement
        if (staleZoneRemap.remappedCount > 0 || textureReconciliation.fixedCount > 0) {
          store.contadorModificaciones = (store.contadorModificaciones || 0) + 1
        }

        // Actualizar sceneBoundingBoxes para los nuevos items
        newItems.forEach((item, id) => {
          if (item.type === 'walls') {
            const wall = item as Walls
            const wallCOBB = getWallCOBB(wall)
            if (wallCOBB) {
              store.sceneBoundingBoxes.set(`COBB_${id}`, {
                type: 'walls',
                boundingBox: wallCOBB,
              })
            }
          } else if (item.type !== 'walls' && item.matrix) {
            // Calcular bounding box para items
            const morphs = (item as Item).morphs || []
            if (morphs.length >= 3) {
              const rotationMatrix4 = new Matrix4().makeRotationFromEuler(
                new Euler(0, item.rotation || 0, 0, 'XYZ'),
              )
              const rotationMatrix = new Matrix3().setFromMatrix4(rotationMatrix4)
              store.sceneBoundingBoxes.set(id, {
                boundingBox: new OBB(
                  toVector3([-morphs[2].default / 2, morphs[1].default / 2, morphs[0].default / 2]),
                  toVector3([morphs[0].default / 2, morphs[1].default / 2, morphs[2].default / 2]),
                  rotationMatrix,
                ),
                type: item.type,
              })
            }
          }

          // Agregar a collisionableItems si corresponde
          const glbData = store.sceneGLB.get(item.catalogID || item.idUnidades)
          if (glbData?.backGLBInfo) {
            if (glbData.backGLBInfo.colision ?? !glbData.backGLBInfo.universal) {
              store.collisionableItems.add(id)
            }
          }
        })

        // Con los items ya en la escena, completar las caras que el remapeo no pudo
        // rellenar (proyectos guardados sin registro) para que el agrupado por
        // snappedSides los vea. Ver rebuildSnappedSides.
        rebuildSnappedSides(store.items, getSnapCandidates(store.items.values()))

        // Actualizar precio sumando el precio del proyecto cargado
        store.price = (store.price || 0) + (info.price || 0)

        // Seleccionar todos los items agregados
        const addedItemIds = Array.from(newItems.keys())
        if (addedItemIds.length > 0) {
          // Deseleccionar todo primero
          store.layers[store.currentLayer].selected.clear()

          // Seleccionar todos los items agregados directamente
          addedItemIds.forEach((id) => {
            store.layers[store.currentLayer].selected.add(id)
          })

          // Establecer el primer item como currentObjectID
          store.currentObjectID = addedItemIds[0]
        }

        // Forzar recálculo de texturas para todos los nuevos items cargados
        // (ObjectMeshENC depende de contadorModificaciones para recalcular handleNodeTextures)
        store.contadorModificaciones =
          (store.contadorModificaciones || 0) +
          1 +
          (textureReconciliation.fixedCount > 0 || staleZoneRemap.remappedCount > 0 ? 1 : 0)
      })

      // Favoritos/layouts antiguos → misma hidratación que proyecto abierto.
      const legacyParentIds = Array.from(newItems.keys()).filter((id) => {
        const it = newItems.get(id)
        return it?.type === 'linkedItem'
      })
      const upgraded = await hydrateLegacyUnitsToDrawItemParity({
        get,
        set,
        onlyParentIds: legacyParentIds,
        projectUnidades: (backResponse as any)?.unidades,
        idVersiones: (backResponse as any)?.disenyo?.idVersiones,
      })
      const postHydrateZoneRemap = remapStaleLinkedZonesOnProjectLoad(get().items, get().sceneGLB)
      if (upgraded || postHydrateZoneRemap.remappedCount > 0) {
        set((store) => {
          store.contadorModificaciones = (store.contadorModificaciones || 0) + 1
        })
      }

      // Seleccionar los items usando la función select después de que termine el set
      // Esto asegura que la selección múltiple funcione correctamente
      const addedItemIds = Array.from(newItems.keys())
      if (addedItemIds.length > 0) {
        const selectFn = get().select
        const setDeltasFn = get().setDeltas
        const currentItems = get().items

        // Limpiar selección primero
        get().unselectAll()

        // Seleccionar todos los items usando la función select
        addedItemIds.forEach((id, index) => {
          // Para el primer item, no usar Ctrl (limpia la selección anterior)
          // Para los siguientes, usar Ctrl=true para agregar a la selección
          if (index === 0) {
            selectFn(id, false)
          } else {
            selectFn(id, true)
          }
        })

        // Calcular y establecer los deltas inmediatamente después de seleccionar
        // Esto es necesario para que funcione en 3D cuando arrastras los items
        if (addedItemIds.length > 1) {
          const selectedItemsSet = new Set(addedItemIds)
          const deltas = calculateDeltasBetweenSelectedItems(selectedItemsSet, currentItems)
          setDeltasFn(deltas)
        }
      }

      // Forzar invalidación de React Three Fiber
      try {
        const r3f = typeof window !== 'undefined' ? (window as any).r3f : undefined
        if (r3f?.invalidate) {
          r3f.invalidate()
          requestAnimationFrame(() => {
            r3f.invalidate()
          })
        }
      } catch (r3fError) {
        // Ignorar errores de invalidación
      }

      // Spinner ya encendido con mensaje (MSG_10) desde MenuItem; aquí solo mantener visible.
      const setGlobalSpinner = get().setGlobalSpinner
      if (typeof setGlobalSpinner === 'function') {
        setGlobalSpinner(true)
      }

      // Pequeño "segundo pulso" de contadorModificaciones para forzar
      // un re-render de texturas cuando ya está todo el GLB cargado.
      setTimeout(() => {
        set((s) => {
          s.contadorModificaciones = (s.contadorModificaciones || 0) + 1
        })
        if (typeof setGlobalSpinner === 'function') {
          setGlobalSpinner(false)
        }
      }, 500)

      // Reintento diferido: si algún item no tenía texturas en el primer cálculo,
      // volver a intentar calcularlas unos ms más tarde (cuando todo debería estar cargado)
      if (retryTextureIds.length > 0) {
        setTimeout(() => {
          const state = get()
          const unidadesRetry = (backResponse as any)?.unidades ?? []
          const sceneGLBRetry = state.sceneGLB
          const itemsRetry = state.items

          retryTextureIds.forEach((id) => {
            const item = itemsRetry.get(id as AnyID) as any
            if (!item) return
            if (item.type === 'walls' || item.type === 'holes') return

            const key = item.catalogID || item.idUnidades
            if (!key) return

            const sceneEntry = sceneGLBRetry.get(key)
            const glb = sceneEntry?.glb as any
            const backGLBInfo = sceneEntry?.backGLBInfo as any
            if (!glb) return

            const unidadFromBack = unidadesRetry.find((u: any) => u.idUnidades === item.idUnidades)
            const acabadosSource =
              (backGLBInfo && Array.isArray(backGLBInfo.acabados) && backGLBInfo.acabados.length > 0
                ? backGLBInfo.acabados
                : undefined) ||
              (unidadFromBack &&
              Array.isArray(unidadFromBack.acabados) &&
              unidadFromBack.acabados.length > 0
                ? unidadFromBack.acabados
                : undefined)

            if (!acabadosSource) return

            const extraInfo =
              unidadFromBack && Array.isArray(unidadFromBack.acabados)
                ? {
                    zonas: unidadFromBack.acabados
                      .filter((a: any) => a && a.idAcabadoUnidad && a.zona)
                      .map((a: any) => ({
                        idAcabadoUnidad: a.idAcabadoUnidad as number,
                        zona: String(a.zona),
                      })),
                  }
                : undefined

            const zonesAndTextures =
              getZonesFromGLB(glb.scene?.children, acabadosSource, extraInfo) || {}
            const { zones = {}, textures = [] } = zonesAndTextures as {
              zones: any
              textures: any[]
            }

            if (textures && textures.length > 0) {
              set((s) => {
                const it = s.items.get(id as AnyID) as any
                if (!it) return
                it.zones = zones
                it.textures = textures
                s.items.set(id as AnyID, it)
                s.contadorModificaciones = (s.contadorModificaciones || 0) + 1
              })
            }
          })
        }, 800)
      }

      return true
    } catch (error) {
      console.error('Error agregando items del proyecto a la escena actual:', error)
      return false
    }
  },
  resetStore: () => {
    const previousGlbs = Array.from(get().sceneGLB.values()) as Array<{ glb?: unknown }>
    const drawLight = (id: string, icon: string, catalogID: string) =>
      get().drawLight(id, icon, catalogID, false)
    set((store) => {
      store.items = new Map()
      store.areas = new Map()
      store.layers = baseLayers
      store.price = 0
      store.sceneBoundingBoxes = new Map()
      store.collisionableItems = new Set()
      store.canDraw = true
      store.currentObjectID = ''
      store.increment = 1
      store.canDraw = true
      store.isModalOpen = false
      store.isProjectOpened = false
      store.currentRoof = ''
      store.currentArea = ''
      store.currentLayer = 0
      store.sceneGLB = new Map()
      store.camera2DPosition = baseCamera2DData
      store.camera3DData = baseCamera3DData
      store.clientInfo = { ...emptyClientInfo, idImpuestos: store.impuestos[0].idImpuestos }
      store.savedViews = []
      store.inspectorDismissed = []
      store.frontalActive = false
      drawLight('lights_baseSceneLight', '', 'LUZGENERAL')
    })
    get().clearAllFreeMeasurements()
    queueMicrotask(() => releasePreviousPlannerGlbs(previousGlbs))
  },

  editMorph: (itemID: string, morphTarget: Item['morphs'][number]['name'], value: number) => {
    set((store: Store) => {
      const item = store.items.get(itemID) as Item

      if (!item) return
      const selectedItems = store.layers[store.currentLayer].selected
      const targetIds =
        usesFreeDimensionScaling(item) && isFreeDimensionDimensionMorph(morphTarget)
          ? [itemID]
          : Array.from(selectedItems)
      targetIds.forEach((selectedId: string) => {
        const item = store.items.get(selectedId) as Item | LinkedItem | Hole
        if (!item) return
        const morphs = item.morphs
        if (!morphs) return
        // if (morphTarget === 'Fondo' && item.tipoUnidNorm === 'V') return
        morphs.forEach((el) => {
          if (el.name === morphTarget) {
            if (item.type === 'holes') {
              el._default = value
              el.default = value > el.max ? el.max : value < el.min ? el.min : value
              if (el.name === 'Fondo' && (item as Hole).tipoUnidNorm === 'P') {
                ;(item as any)[MorphDictionary?.[el.name]] = value + (item as any).offset
              } else {
                ;(item as any)[MorphDictionary?.[el.name]] = value
              }
              return
            }
            if (usesFreeDimensionScaling(item) && isFreeDimensionDimensionMorph(morphTarget)) {
              const layoutValue = value
              const storeValue =
                morphTarget === 'Alto' ? getFreeDimensionStoreAlto(layoutValue, item) : layoutValue
              if (morphTarget === 'Ancho' && isFreeDimensionAnchoApiMorphCatalog(item)) {
                const clamped = clampMorphMm(layoutValue, el.min, el.max)
                el._default = clamped
                el.default = clamped
                item.width = clamped
                return
              }
              if (morphTarget === 'Alto' && itemUsesFreeDimensionAltoApiMorph(item)) {
                const clamped = clampMorphMm(layoutValue, el.min, el.max)
                el._default = clamped
                el.default = clamped
                item.height = clamped
                return
              }
              // MBJ/COL/MAL: morph hasta max API (aspecto clásico) + height/width libres → scale.
              // Conservar _default (= catálogo) para tiradores / ratios; solo actualizar default+store.
              if (usesPureGroupFreeDimensionScale(item)) {
                ensureFreeDimensionCatalogBases(item)
                if (morphHasValidRange(el)) {
                  el.default = clampMorphMm(layoutValue, el.min, el.max)
                } else {
                  el.default = layoutValue
                }
                if (el._default == null || !(Number(el._default) > 0)) {
                  el._default = layoutValue
                }
                if (el.name === 'Fondo' && item.tipoUnidNorm === 'P') {
                  item[MorphDictionary?.[el.name]] = storeValue + item.offset
                } else {
                  item[MorphDictionary?.[el.name]] = storeValue
                }
                return
              }
              const anchoViaMorph =
                morphTarget === 'Ancho' && itemUsesFreeDimensionAnchoGlbMorph(item)
              const altoViaMorph = morphTarget === 'Alto' && itemUsesFreeDimensionAltoGlbMorph(item)
              const fondoViaMorph =
                morphTarget === 'Fondo' && itemUsesFreeDimensionFondoGlbMorph(item)
              if (anchoViaMorph) {
                const morphMax =
                  el.max != null && el.min != null && el.max > el.min ? el.max : layoutValue
                el.default = Math.min(layoutValue, morphMax)
                el._default = layoutValue
                item.width = layoutValue
              } else if (altoViaMorph) {
                const morphMax =
                  el.max != null && el.min != null && el.max > el.min ? el.max : layoutValue
                el._default = layoutValue
                el.default = Math.min(getFreeDimensionAltoGlbMorphMm(layoutValue, item), morphMax)
                item.height = storeValue
              } else if (fondoViaMorph) {
                const morphMax =
                  el.max != null && el.min != null && el.max > el.min ? el.max : layoutValue
                el.default = Math.min(layoutValue, morphMax)
                el._default = layoutValue
                item.thickness = layoutValue
              } else {
                el._default = layoutValue
                if (el.name === 'Fondo' && item.tipoUnidNorm === 'P') {
                  item[MorphDictionary?.[el.name]] = storeValue + item.offset
                } else {
                  item[MorphDictionary?.[el.name]] = storeValue
                }
              }
              if (morphTarget === 'Ancho') {
                logFreeDimensionAncho('editMorph', item as Item, {
                  sidebarValue: layoutValue,
                  storeValue,
                  anchoViaGlbMorph: anchoViaMorph,
                  morphRefMm: el.default,
                  morphActiveMm: el._default,
                })
              }
              if (morphTarget === 'Fondo') {
                logFreeDimensionFondo('editMorph', item as Item, {
                  sidebarValue: layoutValue,
                  storeThickness: item.thickness,
                  fondoViaGlbMorph: fondoViaMorph,
                  morphRefMm: el.default,
                  morphActiveMm: el._default,
                })
              }
              return
            }
            if (!item?.hasMorphs) {
              el._default = value
              return
            }
            // KBR Alto: permitir valor > morph.max (influence 1 + scale en Mesh001/Mesh001_1).
            // Ancho/Fondo/Regleta siguen clampados al rango API.
            if (el.name === 'Alto' && kbrAltoAllowsBeyondMorphMax(item)) {
              el.default = value < el.min ? el.min : value
            } else {
              // Solo actualizar default, NO _default (que mantiene el valor por defecto original)
              el.default = value > el.max ? el.max : value < el.min ? el.min : value
            }
            if (el.name === 'Fondo' && item.tipoUnidNorm === 'P') {
              item[MorphDictionary?.[el.name]] = value + item.offset
            } else if (el.name === 'Regleta' && item.type === 'linkedItem') {
              // Manejo especial para regleta en linkedItems
              ;(item as LinkedItem).regleta = value
            } else {
              // if (el.name === 'Fondo' && item.tipoUnidNorm === 'V') return
              item[MorphDictionary?.[el.name]] = value
            }
          }
        })
        if (
          morphTarget === 'Ancho' ||
          morphTarget === 'Alto' ||
          morphTarget === 'Fondo'
        ) {
          logDimFlow({
            file: 'store/projectActions.ts',
            fn: 'editMorph',
            phase: 'morph.edit',
            reason: `Cambio de dimensión "${morphTarget}" → valor ${value}. Actualiza morph.default/_default y width|height|thickness según modo (free-dim / morph / universal).`,
            snapshot: buildDimFlowSnapshot(item as Item, {
              morphTarget,
              sidebarValue: value,
              usesFreeDimension: usesFreeDimensionScaling(item),
              usesMorph: Boolean(item.hasMorphs && !usesFreeDimensionScaling(item)),
              usesScale: usesFreeDimensionScaling(item) || !item.hasMorphs,
            }),
          })
        }
      })
    })

    setTimeout(() => {
      const item = get().items.get(itemID)
      if (item && item.type === 'holes') {
        get().updateHole(itemID)
      }
      if (
        item &&
        item.type === 'linkedItem' &&
        (morphTarget === 'Alto' ||
          morphTarget === 'Ancho' ||
          morphTarget === 'Fondo' ||
          morphTarget === 'Regleta') &&
        item.childs &&
        item.childs.size > 0
      ) {
        get().updateLinkedSonPositions(itemID, morphTarget as 'Ancho' | 'Fondo' | 'Alto' | 'Regleta')
      }

      // Laterales ya colocados: reescalar/reposicionar al nuevo alto/fondo/ancho.
      if (
        item &&
        item.type === 'linkedItem' &&
        (morphTarget === 'Alto' || morphTarget === 'Fondo' || morphTarget === 'Ancho') &&
        get().lateralsAutoEnabled !== false &&
        !isFreeDimensionLateralOrCostadoCatalog(item as LinkedItem)
      ) {
        const tipo = String((item as LinkedItem).tipoNormalizado ?? '').toUpperCase()
        if (tipo === 'MBJ' || tipo === 'COL' || tipo === 'MAL') {
          const prev = lateralsSyncTimeoutByFurniture.get(itemID)
          if (prev) clearTimeout(prev)
          lateralsSyncTimeoutByFurniture.set(
            itemID,
            setTimeout(() => {
              lateralsSyncTimeoutByFurniture.delete(itemID)
              get().syncLateralsAfterFurnitureDimensionChange?.(itemID)
            }, 250),
          )
        }
      }
    }, 0)
  },

  editMorphs: (morphTarget: Item['morphs'][number]['name'], value: number) => {
    set((store) => {
      const selectedItems = store.currentLayer[store.currentLayer].selected

      selectedItems.forEach((selectedId: string) => {
        const item = store.items.get(selectedId) as Item | LinkedItem | Hole
        if (!item) return

        const morphs = item.morphs
        if (!morphs) return

        morphs.forEach((el) => {
          if (el.name === morphTarget) {
            if (usesFreeDimensionScaling(item) && isFreeDimensionDimensionMorph(morphTarget)) {
              const layoutValue = value
              const storeValue =
                morphTarget === 'Alto' ? getFreeDimensionStoreAlto(layoutValue, item) : layoutValue
              if (morphTarget === 'Ancho' && isFreeDimensionAnchoApiMorphCatalog(item)) {
                const clamped = clampMorphMm(layoutValue, el.min, el.max)
                el._default = clamped
                el.default = clamped
                item.width = clamped
                return
              }
              if (morphTarget === 'Alto' && itemUsesFreeDimensionAltoApiMorph(item)) {
                const clamped = clampMorphMm(layoutValue, el.min, el.max)
                el._default = clamped
                el.default = clamped
                item.height = clamped
                return
              }
              // MBJ/COL/MAL: conservar _default (= catálogo) como en editMorph.
              if (usesPureGroupFreeDimensionScale(item)) {
                ensureFreeDimensionCatalogBases(item)
                if (morphHasValidRange(el)) {
                  el.default = clampMorphMm(layoutValue, el.min, el.max)
                } else {
                  el.default = layoutValue
                }
                if (el._default == null || !(Number(el._default) > 0)) {
                  el._default = layoutValue
                }
                if (el.name === 'Fondo' && item.tipoUnidNorm === 'P') {
                  item[MorphDictionary?.[el.name]] = storeValue + item.offset
                } else {
                  item[MorphDictionary?.[el.name]] = storeValue
                }
                return
              }
              const anchoViaMorph =
                morphTarget === 'Ancho' && itemUsesFreeDimensionAnchoGlbMorph(item)
              const altoViaMorph = morphTarget === 'Alto' && itemUsesFreeDimensionAltoGlbMorph(item)
              const fondoViaMorph =
                morphTarget === 'Fondo' && itemUsesFreeDimensionFondoGlbMorph(item)
              if (anchoViaMorph) {
                const morphMax =
                  el.max != null && el.min != null && el.max > el.min ? el.max : layoutValue
                el.default = Math.min(layoutValue, morphMax)
                el._default = layoutValue
                item.width = layoutValue
              } else if (altoViaMorph) {
                const morphMax =
                  el.max != null && el.min != null && el.max > el.min ? el.max : layoutValue
                el._default = layoutValue
                el.default = Math.min(getFreeDimensionAltoGlbMorphMm(layoutValue, item), morphMax)
                item.height = storeValue
              } else if (fondoViaMorph) {
                const morphMax =
                  el.max != null && el.min != null && el.max > el.min ? el.max : layoutValue
                el.default = Math.min(layoutValue, morphMax)
                el._default = layoutValue
                item.thickness = layoutValue
              } else {
                el._default = layoutValue
                if (el.name === 'Fondo' && item.tipoUnidNorm === 'P') {
                  item[MorphDictionary?.[el.name]] = storeValue + item.offset
                } else {
                  item[MorphDictionary?.[el.name]] = storeValue
                }
              }
              return
            }
            if (!item?.hasMorphs) {
              el._default = value
              return
            }
            el.default = value > el.max ? el.max : value < el.min ? el.min : value
            if (el.name === 'Fondo' && item.tipoUnidNorm === 'P') {
              item[MorphDictionary?.[el.name]] = value + item.offset
              return
            }
            item[MorphDictionary?.[el.name]] = value
          }
        })
      })
    })
  },
  deleteSceneObject: (elementID: string) => {
    set((store) => {
      const itemu = store.items.get(elementID)

      // Si es un itemGroup, borrar el grupo y todos sus hijos
      if (itemu?.type === 'itemGroup') {
        const group = itemu as ItemGroup
        const childrenIds = Array.from(group.children) as AnyID[]

        // Borrar cada hijo del grupo
        childrenIds.forEach((childId) => {
          const child = store.items.get(childId)

          if (child && isLinkedItem(child)) {
            removeLinkedHierarchyFromStore(store, childId)
          }

          // Borrar el hijo
          if (child) {
            const childCatalogID = child.catalogID
            const childCodigo = (child as { codigo?: string }).codigo
            store.layers[store.currentLayer].selected.delete(childId)
            releaseSnappedSides(store.items, childId)
            store.items.delete(childId)
            store.sceneBoundingBoxes.delete(childId)
            store.collisionableItems.delete(childId)
            purgeSceneGlbForCatalog(store, childCatalogID, childCodigo)
          }
        })

        // Borrar el grupo mismo
        store.layers[store.currentLayer].items.delete(elementID)
        store.layers[store.currentLayer].selected.delete(elementID)
        store.items.delete(elementID)
        store.sceneBoundingBoxes.delete(elementID)
        store.collisionableItems.delete(elementID)
        store.currentObjectID = ''
        return
      }

      if (elementID.startsWith('linkedItem_')) {
        const item = store.items.get(elementID)
        if (!isLinkedItem(item)) return

        const catalogID = item.catalogID
        const codigo = (item as { codigo?: string }).codigo
        removeLinkedHierarchyFromStore(store, elementID)

        store.layers[store.currentLayer].selected.delete(elementID)
        store.layers[store.currentLayer].items.delete(elementID)
        releaseSnappedSides(store.items, elementID as AnyID)
        store.items.delete(elementID)
        store.sceneBoundingBoxes.delete(elementID)
        store.collisionableItems.delete(elementID)
        purgeSceneGlbForCatalog(store, catalogID, codigo)
        store.currentObjectID = ''
        return
      }

      if (isWall(itemu)) {
        itemu?.holes.forEach((hole: string) => {
          store.items.delete(hole)
          store.sceneBoundingBoxes.delete(hole)
          store.collisionableItems.delete(hole)
          store.sceneBoundingBoxes.delete(getWallCOBB(hole))
          store.collisionableItems.delete(getWallCOBB(hole))
          store.layers[store.currentLayer].items.delete(hole)
          store.currentObjectID = ''
        })
        itemu.visible = false
        itemu.wallVisible = false
        return
      }

      if (isHole(itemu)) {
        const line = store.items.get(itemu.wall)
        if (isWall(line)) {
          line.holes.delete(elementID)
        }
      }

      if (isItem(itemu) && itemu.snappedWall) {
        const wall = store.items.get(itemu.snappedWall)
        if (isWall(wall)) {
          wall.lowItems.delete(elementID)
          wall.highItems.delete(elementID)
        }
      }

      if (isLinkedItem(itemu)) {
        removeLinkedHierarchyFromStore(store, elementID)
      }

      store.layers[store.currentLayer].selected.delete(elementID)
      store.layers[store.currentLayer].items.delete(elementID)

      const deletedCatalogID = itemu?.catalogID
      const deletedCodigo = itemu ? (itemu as { codigo?: string }).codigo : undefined
      releaseSnappedSides(store.items, elementID as AnyID)
      store.items.delete(elementID)
      store.sceneBoundingBoxes.delete(elementID)
      store.sceneBoundingBoxes.delete(getWallCOBB(elementID))
      store.collisionableItems.delete(getWallCOBB(elementID))
      store.collisionableItems.delete(elementID)
      purgeSceneGlbForCatalog(store, deletedCatalogID, deletedCodigo)
      store.currentObjectID = ''

      // console.log(`Deleted item: ${elementID}`)
    })
  },
  testFn: () => {
    set((store: Store) => {
      const object = store.items.get('someID')
      if (!object || object.type !== 'items') return
    })
  },
  openModal: () => {
    set((store: Store) => {
      store.isModalOpen = true
    })
  },
  closeModal: () => {
    set((store: Store) => {
      store.isModalOpen = false
    })
  },
  openProyect: () => {
    set((store: Store) => {
      store.isProjectOpened = true
    })
  },
  setImpuestos: (impuestos: Store['impuestos']) => {
    set((store) => {
      if (!impuestos) return
      store.impuestos = impuestos
      store.clientInfo.idImpuestos = impuestos.find((el) => el.porDefecto)?.idImpuestos ?? 0
    })
  },
  editCameraBoundingBox: (position: Vector3, rotation: Euler) => {
    if (isLightweightViewer()) return
    set((store) => {
      const camObb = store.cameraOBB
      if (!camObb) return
      let dist = 0
      // Verificar si la posición está dentro del área formada por las paredes
      const walls = Array.from(store.items.values()).filter((item) => item.type === 'walls')
      const wallPoints = walls.flatMap((wall) => [wall.start, wall.end])
      // Algoritmo simple: proyectar en XZ y usar ray-casting para saber si está dentro del polígono
      const isPointInPolygon = (point: { x: number; z: number }, polygon: number[][]) => {
        const x = point.x,
          z = point.z
        let inside = false
        for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
          const xi = polygon[i][0],
            zi = polygon[i][1]
          const xj = polygon[j][0],
            zj = polygon[j][1]
          const intersect = zi > z !== zj > z && x < ((xj - xi) * (z - zi)) / (zj - zi || 1) + xi
          if (intersect) inside = !inside
        }
        return inside
      }
      const polygon = wallPoints.map(([x, z]) => [x, z])
      if (!isPointInPolygon(position, polygon)) {
        // Calcular el centro del polígono en XZ
        let centerX = 0,
          centerZ = 0
        if (polygon.length > 0) {
          for (const [x, z] of polygon) {
            centerX += x
            centerZ += z
          }
          centerX /= polygon.length
          centerZ /= polygon.length
        }
        // La posición de la cámara es position.x, position.z
        // Calcular la distancia en XZ
        const dx = position.x - centerX
        const dz = position.z - centerZ
        dist = Math.sqrt(dx * dx + dz * dz)
      }
      if (typeof dist != 'number') {
        dist = Number(dist)
      }
      const m4 = new Matrix4().makeRotationFromEuler(rotation)
      const m3 = new Matrix3().setFromMatrix4(m4)
      camObb.set(position, toVector3([400, 50, dist]), m3)
    })
  },
  projectItemOnWall: (
    itemID: AnyID,
    wallID: AnyID,
    matrix: Matrix4,
    _skipContiguityCheck = false,
    preserveY = false,
  ) => {
    set((store) => {
      const item = store.items.get(itemID) as Item | LinkedItem
      if (!item) return
      if (
        (item.catalogID && item.catalogID.toString().startsWith('CORTEKIT')) ||
        isAccesorioFregaderoCatalog(item.catalogID)
      ) {
        return // Saltar procesamiento para kits de corte y accesorios de fregadero
      }
      const wall = store.items.get(wallID) as Walls
      if (!wall) return

      // La pared como OBB: la línea start→end es su cara frontal y el cuerpo se
      // extiende `thickness` hacia atrás (ver editWall: el centro queda desplazado
      // thickness/2 en la perpendicular de la línea).
      const start = toVector3(wall.start)
      const end = toVector3(wall.end)
      start.y = 0
      end.y = 0
      const wallXAxis = end.clone().sub(start)
      const wallLength = wallXAxis.length()
      if (wallLength === 0) return
      wallXAxis.normalize()
      const wallQuat = new Quaternion().setFromUnitVectors(new Vector3(1, 0, 0), wallXAxis)
      const wallZAxis = new Vector3(0, 0, 1).applyQuaternion(wallQuat)
      const wallCenter = start
        .clone()
        .add(end)
        .multiplyScalar(0.5)
        .sub(wallZAxis.clone().multiplyScalar((wall.thickness || 0) / 2))

      projectItemOnSlabFaces(
        store,
        itemID,
        wallID,
        wall,
        matrix,
        wallQuat,
        wallCenter,
        wallLength / 2,
        preserveY,
      )

      // Forzar actualización de texturas de encimera después de colocar el mueble
      // SOLO si es MBJ (no para COL ni MAL)
      if (item.tipoNormalizado === 'MBJ') {
        store.contadorModificaciones = store.contadorModificaciones + 1
      }
    })
  },
  drawItemGroup: async (idUnidadesAgrupadas: number, matrix?: Matrix4): Promise<AnyID | null> => {
    try {
      // 1. Obtener la unidad agrupada del backend
      const response = await getUnidadAgrupada(idUnidadesAgrupadas)

      // 2. Parsear el JSON con SuperJSON
      const parsed = SuperJSON.parse(response.escena.json)

      // La estructura puede ser parsed.json o parsed directamente
      const escenaData = (parsed.json || parsed) as {
        group: ItemGroup
        items: Record<string, Item | LinkedItem>
        linkedSons: Record<string, any>
      }

      const { group, items, linkedSons } = escenaData

      // 3. Crear mapeo de IDs antiguos a nuevos
      const idMapping = new Map<string, AnyID>()
      const drawnItems: AnyID[] = []

      // 4. Dibujar cada linkedItem usando drawItem (carga el GLB)
      // IMPORTANTE: Pasamos skipLinkedSons: true para evitar crear linkedSons automáticamente
      // Los linkedSons se dibujarán manualmente en el paso 5 con los datos guardados
      for (const [oldId, itemData] of Object.entries(items)) {
        const item = itemData as LinkedItem

        // Detectar el tipo original del item para generar el ID correcto
        const itemType = item.type === 'items' ? 'items' : 'linkedItem'

        // Usar catalogID para cargar el GLB, no idUnidades
        const newId = await get().drawItem(
          getId(itemType),
          item.catalogID || String(item.idUnidades),
          item.image || '',
          true,
          null,
          itemType,
          undefined, // realCatalogID
          false, // shouldGoToBackHeight
          true, // skipLinkedSons - NO crear linkedSons automáticamente
        )

        idMapping.set(oldId, newId)
        drawnItems.push(newId)

        // Reconstruir matriz desde JSON
        const itemMatrix = new Matrix4()
        if (item.matrix && (item.matrix as any).elements) {
          itemMatrix.fromArray((item.matrix as any).elements)
        }

        // Aplicar las propiedades guardadas con editItem
        get().editItem(newId, {
          matrix: itemMatrix,
          textures: item.textures,
          zones: item.zones,
          x: item.x,
          y: item.y,
          z: item.z,
          rotation: item.rotation,
          width: item.width,
          height: item.height,
          thickness: item.thickness,
          selected: false,
          visible: true,
          type: itemType, // Preservar el tipo original
          childs: new Set(), // Inicializar como Set vacío, se actualizará en paso 6 si hay hijos
        } as any)

        // IMPORTANTE: Sincronizar morphs con las dimensiones restauradas
        if (item.width !== undefined && item.width > 0) {
          get().editMorph(newId, 'Ancho', item.width)
        }
        if (item.height !== undefined && item.height > 0) {
          get().editMorph(newId, 'Alto', item.height)
        }
        if (item.thickness !== undefined && item.thickness > 0) {
          get().editMorph(newId, 'Fondo', item.thickness)
        }
      }

      // 5. Dibujar cada linkedSon desde el JSON guardado
      if (linkedSons && Object.keys(linkedSons).length > 0) {
        for (const [oldSonId, sonData] of Object.entries(linkedSons)) {
          const son = sonData as any

          // Usar catalogID para cargar el GLB, no idUnidades
          const newSonId = await get().drawItem(
            getId('linkedSon'),
            son.catalogID || String(son.idUnidades),
            son.image || '',
            true,
            null,
            'linkedSon',
            undefined, // realCatalogID
            false, // shouldGoToBackHeight
            true, // skipLinkedSons - NO crear linkedSons anidados
          )

          idMapping.set(oldSonId, newSonId)

          // Reconstruir matriz
          const sonMatrix = new Matrix4()
          if (son.matrix && (son.matrix as any).elements) {
            sonMatrix.fromArray((son.matrix as any).elements)
          }

          // Actualizar firstParentID con el nuevo ID
          let newFirstParentID = son.firstParentID
          if (son.firstParentID) {
            const mappedParentId = idMapping.get(son.firstParentID)
            if (mappedParentId) {
              newFirstParentID = mappedParentId
            }
          }

          // Aplicar propiedades del hijo
          get().editItem(newSonId, {
            matrix: sonMatrix,
            textures: son.textures,
            zones: son.zones,
            x: son.x,
            y: son.y,
            z: son.z,
            rotation: son.rotation,
            width: son.width,
            height: son.height,
            thickness: son.thickness,
            firstParentID: newFirstParentID,
            depth: son.depth,
            selected: false,
            visible: true,
            type: 'linkedSon', // Asegurar que el tipo sea correcto
            childs: new Set(), // Inicializar como Set vacío
          } as any)

          // IMPORTANTE: Sincronizar morphs con las dimensiones restauradas para linkedSons
          if (son.width !== undefined && son.width > 0) {
            get().editMorph(newSonId, 'Ancho', son.width)
          }
          if (son.height !== undefined && son.height > 0) {
            get().editMorph(newSonId, 'Alto', son.height)
          }
          if (son.thickness !== undefined && son.thickness > 0) {
            get().editMorph(newSonId, 'Fondo', son.thickness)
          }
        }
      }

      // 6. Actualizar las referencias de childs en linkedItems
      for (const [oldId, itemData] of Object.entries(items)) {
        const item = itemData as LinkedItem
        const childCount =
          item.childs instanceof Set ? item.childs.size : (item.childs?.length ?? 0)
        if (item.childs && childCount > 0) {
          const newId = idMapping.get(oldId)
          if (newId) {
            const updatedChilds = new Set<string>()

            const childsArray = item.childs instanceof Set ? Array.from(item.childs) : item.childs
            childsArray.forEach((oldChildId) => {
              const newChildId = idMapping.get(oldChildId as string)
              if (newChildId) {
                updatedChilds.add(newChildId as string)
              }
            })

            // Actualizar childs del item
            get().editItem(newId, {
              childs: updatedChilds,
            } as any)
          }
        }
      }

      // 7. Crear el grupo con los nuevos IDs
      const newGroupId = getId('itemGroup') as ItemGroupID

      const newChildren = new Set<AnyID>()
      group.children.forEach((oldChildId) => {
        const newChildId = idMapping.get(oldChildId as string)
        if (newChildId) {
          newChildren.add(newChildId)
        }
      })

      // Reconstruir matriz del grupo
      const groupMatrix = new Matrix4()
      if (group.matrix && (group.matrix as any).elements) {
        groupMatrix.fromArray((group.matrix as any).elements)
      }

      // Usar la matriz proporcionada o la del grupo
      const finalMatrix = matrix ? matrix.clone() : groupMatrix

      // Extraer posición
      const position = new Vector3()
      const quaternion = new Quaternion()
      const scale = new Vector3()
      finalMatrix.decompose(position, quaternion, scale)

      // Crear el grupo en el store
      set((store) => {
        const newGroup: ItemGroup = {
          ...group,
          id: newGroupId,
          children: newChildren,
          matrix: finalMatrix,
          x: position.x,
          y: position.y,
          z: position.z,
          selected: true,
          visible: true,
          canCollide: true,
          idUnidadesAgrupadas: idUnidadesAgrupadas, // Guardar el ID del backend
        }

        store.items.set(newGroupId, newGroup as any)
        store.layers[store.currentLayer].items.add(newGroupId)

        // IMPORTANTE: Transformar items hijos al espacio local del grupo
        const groupInverse = finalMatrix.clone().invert()
        newChildren.forEach((childId) => {
          const child = store.items.get(childId)
          if (child && child.matrix) {
            // Convertir matriz de mundo a matriz local respecto al grupo
            const localMatrix = groupInverse.clone().multiply(child.matrix)

            const pos = new Vector3()
            const rot = new Quaternion()
            const scale = new Vector3()
            localMatrix.decompose(pos, rot, scale)

            // Actualizar el hijo con su matriz local
            ;(child as any).matrix = localMatrix
            ;(child as any).x = pos.x
            ;(child as any).y = pos.y
            ;(child as any).z = pos.z
            ;(child as any).isBlocked = true
          }
        })

        // Mover el grupo al origen (0,0,0) manteniendo su rotación
        const newPosition = new Vector3(0, 0, 0)
        const matrixAt000 = new Matrix4().compose(newPosition, quaternion, scale)

        // Actualizar la matriz y posición del grupo
        newGroup.matrix = matrixAt000
        newGroup.x = 0
        newGroup.y = 0
        newGroup.z = 0

        // Computar y añadir OBB del grupo con la nueva matriz en 0,0,0
        const groupOBBResult = computeItemGroupOBB(newChildren, store.items, matrixAt000)
        if (groupOBBResult) {
          const { center, halfSize, rotation } = groupOBBResult
          const groupOBB = new OBB(center, halfSize, rotation)
          store.sceneBoundingBoxes.set(newGroupId, {
            boundingBox: groupOBB,
            type: 'items',
          })
        }

        // Eliminar OBBs individuales de todos los items dibujados (linkedItems y linkedSons)
        // Solo debe quedar la OBB del grupo
        idMapping.forEach((newId) => {
          store.sceneBoundingBoxes.delete(newId)
        })

        // También eliminar la OBB del grupo antiguo si existe
        if (group.id && store.sceneBoundingBoxes.has(group.id as AnyID)) {
          store.sceneBoundingBoxes.delete(group.id as AnyID)
        }

        // Eliminar items individuales de collisionableItems
        // Solo el grupo debe poder colisionar
        idMapping.forEach((newId) => {
          store.collisionableItems.delete(newId)
        })

        // Añadir a collisionables solo el grupo
        store.collisionableItems.add(newGroupId)

        // Seleccionar el grupo
        store.currentObjectID = newGroupId
        store.layers[store.currentLayer].selected.clear()
        store.layers[store.currentLayer].selected.add(newGroupId)
      })

      return newGroupId
    } catch (error) {
      console.error('[drawItemGroup] ❌ Error:', error)
      return null
    }
  },
  editZone: async (id: AnyID, zoneToPasteTo: string, _copiedMaterial: Zone[string]['material']) => {
    set((store) => {
      const sceneObject = store.items.get(id) ?? store.areas.get(id)
      if (!sceneObject)
        return // console.log('No scene object found on editting zone', { ...sceneObject })

      const textureOfTheZoneToPasteTo = sceneObject.textures.find(
        (el) => el.zoneName === zoneToPasteTo,
      )
      if (!textureOfTheZoneToPasteTo) {
        return
      }
      if (!_copiedMaterial) return // console.log('Not material found', { _copiedMaterial })
      sceneObject.zones[zoneToPasteTo].material = { ..._copiedMaterial }
      textureOfTheZoneToPasteTo.groupID = _copiedMaterial?.groupID
      textureOfTheZoneToPasteTo.name = _copiedMaterial?.name
      textureOfTheZoneToPasteTo.searchID = _copiedMaterial?.searchID
      textureOfTheZoneToPasteTo.url = _copiedMaterial?.url
      textureOfTheZoneToPasteTo.id = _copiedMaterial?.id
      textureOfTheZoneToPasteTo.modelo = _copiedMaterial?.modelo
      textureOfTheZoneToPasteTo.acabado = _copiedMaterial?.acabado
    })
  },
  toggleVisibility: (elementID: string) => {
    set((store) => {
      const item = store.items.get(elementID) ?? store.areas.get(elementID)
      if (item) {
        item.visible = !item.visible
      }
    })
  },
  setExclusiveVisibility: (idsToKeepVisible: Set<string>, hideDimentions3D: boolean) => {
    set((store) => {
      store.items.forEach((item, id) => {
        if (item.type === 'lights') return
        const isVisible = idsToKeepVisible.has(id)
        item.visible = isVisible
        if (hideDimentions3D) {
          if (item.type === 'walls' || item.type === 'partition' || item.type === 'hiddenWall') {
            item.wallVisible = isVisible
          }
        }
      })
      store.areas.forEach((area, id) => {
        area.visible = idsToKeepVisible.has(id)
      })
    })
  },
  toggleCollision: (id: string) => {
    set((store) => {
      const item = store.items.get(id)
      if (!item || !id) return // console.log('Cant find item on collision')
      item.canCollide = !item?.canCollide
      const collisionableItems = store.collisionableItems
      if (!item.canCollide) {
        collisionableItems.delete(id)
      } else {
        collisionableItems.add(id)
      }
    })
  },
  toggleIsBlocked: (selectedItems: Set<string>) => {
    set((store) => {
      selectedItems.forEach((id) => {
        const item = store.items.get(id)
        if (!item || !id) return // console.log('Cant find item on blocking')
        item.isBlocked = !item?.isBlocked
      })
    })
  },
  // TODO: esto probablemente sobra con lo de rafa
  setShouldUpdateCamera: (val: boolean) => {
    set((store) => {
      store.shouldUpdateCamera = val
    })
  },
  // TODO: esto probablemente sobra con lo de rafa

  setCameraPosition(y: number | string) {
    set((store) => {
      store.camera2DPosition.zoom = parseInt(y) / 1000
    })
  },
  setFrameloop: (frameloop: Store['frameloop']) => {
    set((store) => {
      store.frameloop = frameloop
    })
  },
  // TODO: esto probablemente sobra o hay que rehacerlo
  addIncrement: () => {
    set((s) => {
      s.increment = s.increment + 1
    })
  },
  addContadorModificaciones: () => {
    set((s) => {
      s.contadorModificaciones = s.contadorModificaciones + 1
    })
  },
})